import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { MarkdownRenderer } from "@/components/public/MarkdownRenderer";
import { AdSlot } from "@/components/public/AdSlot";
import { SeoHead } from "@/components/public/SeoHead";
import { JsonLd } from "@/components/public/JsonLd";

export default function DynamicCmsPage() {
  const { slug } = useParams();
  const { data, isLoading, error } = useQuery({
    queryKey: ["cms", slug],
    queryFn: async () => (await api.get(`/public/cms/${slug}`)).data,
    retry: false,
  });

  if (isLoading) return <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 text-muted_text">Loading…</div>;
  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 text-center">
        <h1 className="editorial-headline text-5xl mb-4">Page not found</h1>
        <p className="text-muted_text">This page is either unpublished or doesn't exist.</p>
      </div>
    );
  }

  const origin = typeof window !== "undefined" ? window.location.origin : "https://sustainablycurated.com";
  const pageUrl = `${origin}/${slug}`;
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: data.title,
      description: data.seo_description || undefined,
      image: data.featured_image_url || undefined,
      url: pageUrl,
      datePublished: data.created_at || undefined,
      dateModified: data.updated_at || data.created_at || undefined,
      author: { "@type": "Person", name: "Teresa Cordeiro" },
      publisher: {
        "@type": "Organization",
        name: "Sustainably Curated",
        logo: { "@type": "ImageObject", url: `${origin}/leaf.svg` },
      },
      mainEntityOfPage: { "@type": "WebPage", "@id": pageUrl },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: origin + "/" },
        { "@type": "ListItem", position: 2, name: data.title, item: pageUrl },
      ],
    },
  ];

  return (
    <article className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="cms-page">
      <SeoHead title={data.seo_title || `${data.title} — Sustainably Curated`} description={data.seo_description || undefined} path={`/${slug}`} image={data.featured_image_url} />
      <JsonLd data={jsonLd} />
      {data.featured_image_url && <img src={data.featured_image_url} alt={data.title} className="w-full aspect-[16/7] object-cover rounded-3xl mb-6" />}
      <h1 className="text-[1.75rem] uppercase tracking-[0.25em] text-leaf font-medium mb-6">{data.title}</h1>
      <MarkdownRenderer source={data.content} />
      <AdSlot slotKey="cms_inline" />
    </article>
  );
}
